import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {api} from '../lib/api';
import {statsApi} from '../lib/api-resources';
import type {DashboardStats} from '../lib/types';

interface HealthInfo {
  status: string;
  version: string;
  environment: string;
  timestamp: string;
}

export default function DashboardPage() {
  const {t} = useTranslation();
  const [health, setHealth] = useState<HealthInfo | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<HealthInfo>('/api/v1/health').then(setHealth).catch(() => undefined);
    statsApi
      .dashboard()
      .then(setStats)
      .catch((e) => setError(String(e)));
  }, []);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>{t('dashboard.title')}</h1>
          <p className="page-subtitle">{t('dashboard.subtitle')}</p>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      {stats && (
        <>
          <section className="kpi-grid">
            <KpiCard label={t('dashboard.kpi.customers')} value={stats.customers} accent="blue" />
            <KpiCard label={t('dashboard.kpi.sites')} value={stats.sites} accent="purple" />
            <KpiCard label={t('dashboard.kpi.servers')} value={stats.servers} accent="teal" />
            <KpiCard label={t('dashboard.kpi.managedSites')} value={stats.sites_managed_hosting} accent="green" />
            <KpiCard label={t('dashboard.kpi.customerServerSites')} value={stats.sites_customer_server} accent="amber" />
          </section>

          <section className="grid">
            <div className="card">
              <h3>{t('dashboard.servers.title')}</h3>
              <dl>
                <dt>{t('dashboard.servers.self')}</dt>
                <dd>{stats.servers_self}</dd>
                <dt>{t('dashboard.servers.customer')}</dt>
                <dd>{stats.servers_customer}</dd>
                <dt>{t('dashboard.servers.byStatus')}</dt>
                <dd>
                  {Object.keys(stats.servers_by_status).length === 0
                    ? t('dashboard.servers.empty')
                    : Object.entries(stats.servers_by_status).map(([k, v]) => (
                        <div key={k}>
                          <span className="badge muted">{k}</span> {v}
                        </div>
                      ))}
                </dd>
              </dl>
            </div>
            <div className="card">
              <h3>{t('dashboard.sitesByStatus.title')}</h3>
              {Object.keys(stats.sites_by_status).length === 0 ? (
                <p className="muted">{t('dashboard.sitesByStatus.empty')}</p>
              ) : (
                <ul className="status-list">
                  {Object.entries(stats.sites_by_status).map(([k, v]) => (
                    <li key={k}>
                      <span className="badge muted">{k}</span>
                      <span className="num">{v}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="card">
              <h3>{t('dashboard.backend.title')}</h3>
              {health ? (
                <dl>
                  <dt>{t('common.status')}</dt>
                  <dd>
                    <span className="badge ok">{health.status}</span>
                  </dd>
                  <dt>{t('dashboard.backend.version')}</dt>
                  <dd>{health.version}</dd>
                  <dt>{t('dashboard.backend.environment')}</dt>
                  <dd>{health.environment}</dd>
                </dl>
              ) : (
                <p className="muted">{t('dashboard.backend.loading')}</p>
              )}
            </div>
            <div className="card">
              <h3>{t('dashboard.phaseProgress.title')}</h3>
              <ol className="phase-list">
                <li className="done">{t('dashboard.phaseProgress.phases.phase0')} ✓</li>
                <li className="done">{t('dashboard.phaseProgress.phases.phase1')} ✓</li>
                <li>{t('dashboard.phaseProgress.phases.phase2')}</li>
                <li>{t('dashboard.phaseProgress.phases.phase3')}</li>
                <li>{t('dashboard.phaseProgress.phases.phase4')}</li>
                <li>{t('dashboard.phaseProgress.phases.phase5')}</li>
                <li>{t('dashboard.phaseProgress.phases.phase6')}</li>
                <li>{t('dashboard.phaseProgress.phases.phase7')}</li>
                <li>{t('dashboard.phaseProgress.phases.phase8')}</li>
                <li>{t('dashboard.phaseProgress.phases.phase9')}</li>
                <li>{t('dashboard.phaseProgress.phases.phase10')}</li>
                <li>{t('dashboard.phaseProgress.phases.phase11')}</li>
                <li>{t('dashboard.phaseProgress.phases.phase12')}</li>
                <li>{t('dashboard.phaseProgress.phases.phase13')}</li>
                <li>{t('dashboard.phaseProgress.phases.phase14')}</li>
                <li>{t('dashboard.phaseProgress.phases.phase15')}</li>
              </ol>
            </div>
          </section>
        </>
      )}

      {!stats && !error && <p className="muted">{t('common.loadingStats')}</p>}
    </div>
  );
}

function KpiCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent: 'blue' | 'purple' | 'teal' | 'green' | 'amber';
}) {
  return (
    <div className={`kpi kpi-${accent}`}>
      <div className="kpi-value">{value}</div>
      <div className="kpi-label">{label}</div>
    </div>
  );
}
